import date from "date-and-time";
import { useTranslation } from "react-i18next";

import usePricingAccess from "../../hooks/usePricingAccess";
import { IContactLeadSummary } from "../../models/Contact";
import { LeadSource, LeadStatus } from "../../models/Lead";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import PriceFormat from "../../utils/priceFormat";
import { MergedFromBadge } from "./MergeSourceBadges";

/**
 * The other leads that would be changed by an edit to a shared contact.
 *
 * Two layouts, because the two places this appears have very different widths: "list" stacks each
 * lead into a narrow two-line row for the contact card (which sits in a sidebar column), "table"
 * spreads the same fields into columns for the warning modal.
 */
export default function SharedContactLeadsTable({
  leads,
  variant = "list",
}: {
  leads: IContactLeadSummary[];
  variant?: "list" | "table";
}) {
  const { t } = useTranslation("accounts");
  const { canViewPrices } = usePricingAccess();

  if (leads.length === 0) return null;

  if (variant === "list") {
    return (
      <ul className="max-h-56 divide-y divide-gray-200 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {leads.map((lead) => (
          <li key={lead.id}>
            <button
              type="button"
              onClick={() => openInNewTab(`/leads/${lead.id}`)}
              className="flex w-full items-start justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">{lead.vrm ?? t("No VRM")}</p>
                <p className="truncate text-xs text-gray-500">
                  {[lead.make, lead.model].filter(Boolean).join(" ") || t("Unknown vehicle")}
                </p>
                {lead.mergedFrom && (
                  <div className="mt-1">
                    <MergedFromBadge mergedFrom={lead.mergedFrom} />
                  </div>
                )}
              </div>
              <div className="shrink-0 text-right">
                <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                  {t(LeadStatus[lead.status], { ns: "leads" })}
                </span>
                <p className="mt-0.5 text-xs text-gray-500">
                  {canViewPrices && <span>{PriceFormat(lead.price)} &middot; </span>}
                  {date.format(new Date(lead.created), "DD/MM/YY")}
                </p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="max-h-64 overflow-auto rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky top-0 bg-primary-50">
          <tr className="text-left text-gray-800">
            <th className="whitespace-nowrap px-3 py-2 text-sm font-semibold">{t("VRM")}</th>
            <th className="px-3 py-2 text-sm font-semibold">{t("Make & Model")}</th>
            <th className="whitespace-nowrap px-3 py-2 text-sm font-semibold">{t("Status")}</th>
            {canViewPrices && (
              <th className="whitespace-nowrap px-3 py-2 text-sm font-semibold">{t("Price")}</th>
            )}
            <th className="hidden whitespace-nowrap px-3 py-2 text-sm font-semibold sm:table-cell">
              {t("Source")}
            </th>
            <th className="hidden whitespace-nowrap px-3 py-2 text-sm font-semibold sm:table-cell">
              {t("Created")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {leads.map((lead, index) => (
            <tr
              key={lead.id}
              onClick={() => openInNewTab(`/leads/${lead.id}`)}
              className={classNames(
                index % 2 === 1 && "bg-gray-50",
                "hover:cursor-pointer hover:bg-gray-100"
              )}
            >
              <td className="whitespace-nowrap px-3 py-2 text-sm font-medium">{lead.vrm ?? t("N/A")}</td>
              <td className="px-3 py-2 text-sm">
                {[lead.make, lead.model].filter(Boolean).join(" ") || t("Unknown vehicle")}
                {lead.mergedFrom && (
                  <div className="mt-1">
                    <MergedFromBadge mergedFrom={lead.mergedFrom} />
                  </div>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-sm">{t(LeadStatus[lead.status], { ns: "leads" })}</td>
              {canViewPrices && (
                <td className="whitespace-nowrap px-3 py-2 text-sm">{PriceFormat(lead.price)}</td>
              )}
              <td className="hidden whitespace-nowrap px-3 py-2 text-sm sm:table-cell">
                {t(LeadSource[lead.website], { ns: "leads" })}
              </td>
              <td className="hidden whitespace-nowrap px-3 py-2 text-sm sm:table-cell">
                {date.format(new Date(lead.created), "DD/MM/YYYY")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
